// #region: Class definition *********************************************************************/
/**
 * Provides GPS location functionality using the browser's native Geolocation API.
 * Handles location permissions and retrieves device position data with error handling.
 * 
 * @example
 * // Get current position
 * const position = await gps.getCurrentPosition();
 * console.log(`Latitude: ${position.latitude}, Longitude: ${position.longitude}`);
 * 
 * @example
 * // Handle errors
 * try {
 *     const position = await gps.getCurrentPosition({ timeout: 5000 });
 * }
 * catch (error) {
 *     console.error(`Location error: ${error.message}`);
 * }
 */
window.gps = new class {

    // #region: Private Fields *******************************************************************/
    /**
     * SessionStorage key for cached GPS position. Shared with other callers so a single
     * location fix is reused across the session, avoiding redundant permission prompts and
     * battery drain on iOS/Android.
     */
    #cacheKey = 'leanforms_gps_cache';

    /**
     * Cache TTL in milliseconds.
     * Typical inspections take half an hour or more, let's take 40 minutes.
     */
    #cacheTtl = 40 * 60 * 1000;
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Identifies class.
     * @returns {string} The string representation of this class.
     */
    toString() {
        return "[gps]";
    }

    static toString() {
        return "[gps]";
    }

    /** 
     * Identifies the version.
     * @returns {string} The version number of this class.
     */
    static version() {
        return "1.0.0";
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Gets a GPS position, returning a cached value from sessionStorage when fresh enough.
     * Falls back to a live read and caches the result. Use this in preference to
     * getCurrentPosition whenever a coarse, session-scoped location is acceptable — it
     * prevents repeated permission prompts and wake-ups of the GPS radio.
     * @param {Object} [options] Optional configuration. Same shape as getCurrentPosition options,
     *   plus `maxCacheAge` (ms) to override the default cache TTL for this call.
     * @returns {Promise<Object>} Position object (same shape as getCurrentPosition). If the live
     *   read fails but a cached position exists (even if stale), the cached value is returned
     *   rather than throwing. Only throws if no cache exists AND the live read fails.
     */
    async getCachedPosition(options = {}) {
        const maxCacheAge = typeof options.maxCacheAge === 'number' ? options.maxCacheAge : this.#cacheTtl;
        const cached = this.#readCache();

        if (cached && (Date.now() - cached.timestamp) < maxCacheAge) {
            return cached;
        }

        try {
            const position = await this.getCurrentPosition({
                enableHighAccuracy: false,
                maximumAge: maxCacheAge,
                timeout: 10000,
                ...options
            });

            this.#writeCache(position);

            return position;
        }
        catch (error) {
            if (cached) {
                return cached;
            }

            throw error;
        }
    }

    /**
     * Gets the current GPS position of the device.
     * Requests permission if not yet granted and returns position data.
     * @param {Object} [options] Optional configuration for the geolocation request.
     * @param {boolean} [options.enableHighAccuracy=true] Whether to request high accuracy position.
     * @param {number} [options.maximumAge=0] Maximum age of cached position in milliseconds.
     * @param {number} [options.timeout=10000] Timeout for position acquisition in milliseconds.
     * @returns {Promise<Object>} Promise resolving to position object with latitude, longitude, accuracy, altitude, altitudeAccuracy, heading, speed, and timestamp.
     * @throws {Error} Throws error if geolocation is not supported, permission denied, position unavailable, or timeout.
     */
    async getCurrentPosition(options = {}) {

        // Merge options with defaults.
        const geolocationOptions = {
            enableHighAccuracy: true,
            maximumAge: 0,
            timeout: 10000,
            ...options
        };

        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                // Success callback.
                (position) => {
                    const positionData = {
                        accuracy: position.coords.accuracy,
                        altitude: position.coords.altitude,
                        altitudeAccuracy: position.coords.altitudeAccuracy,
                        heading: position.coords.heading,
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        speed: position.coords.speed,
                        timestamp: position.timestamp
                    };

                    resolve(positionData);
                },
                // Error callback.
                (error) => {
                    let errorMessage;
                    let errorType;

                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            errorMessage = 'Location permission denied. Please enable location access for this application in your browser settings.';
                            errorType = 'PERMISSION_DENIED';

                            break;

                        case error.POSITION_UNAVAILABLE:
                            errorMessage = 'Location information is unavailable. Please ensure GPS/Location services are enabled on your device.';
                            errorType = 'POSITION_UNAVAILABLE';

                            break;

                        case error.TIMEOUT:
                            errorMessage = `Location request timed out after ${geolocationOptions.timeout / 1000} seconds. Try again with better signal or move to an open area.`;
                            errorType = 'TIMEOUT';

                            break;

                        default:
                            errorMessage = `An unknown error occurred: ${error.message}`;
                            errorType = 'UNKNOWN';

                            break;
                    }

                    const gpsError = new Error(errorMessage);

                    gpsError.type = errorType;
                    gpsError.code = error.code;

                    reject(gpsError);
                },
                geolocationOptions
            );
        });
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Reads a cached position from sessionStorage.
     * @returns {Object|null} Cached position (with timestamp) or null if absent/corrupt.
     *
     * @private
     */
    #readCache() {
        try {
            const raw = sessionStorage.getItem(this.#cacheKey);

            if (!raw) {
                return null;
            }

            const parsed = JSON.parse(raw);

            if (!parsed || typeof parsed.timestamp !== 'number') {
                return null;
            }

            return parsed;
        }
        catch {
            return null;
        }
    }

    /**
     * Writes a position to the sessionStorage cache.
     * @param {Object} position Position object from getCurrentPosition.
     *
     * @private
     */
    #writeCache(position) {
        try {
            sessionStorage.setItem(this.#cacheKey, JSON.stringify({
                accuracy: position.accuracy,
                altitude: position.altitude,
                altitudeAccuracy: position.altitudeAccuracy,
                heading: position.heading,
                latitude: position.latitude,
                longitude: position.longitude,
                speed: position.speed,
                timestamp: Date.now()
            }));
        }
        catch {
            // Storage quota or private-mode errors are non-fatal.
        }
    }
    // #endregion ********************************************************************************/
}();
// #endregion ************************************************************************************/
